/**
 * Per-item tag-status helpers for the Resume Editor.
 *
 * Mirror of the backend's `shared/tag_status.py` (ITEMS_TAG_COUNT_RANGES + the
 * item-eligibility gate in gen_tags._per_item_jobs): an item below its section
 * minimum is (re)tagged on the next save; an item at/above it keeps its tags.
 * Extracted from ResumeEditor.tsx so the rules are unit-testable in isolation
 * — the editor imports them, nothing else changed.
 */
import type { ResumeJSON } from '../types';

export const TAG_MIN_BULLET = 10;
export const TAG_MIN_ITEM = 5;
// Summary v2 subsection entries tag LOWER than the rest (features/summary_v2.md:
// 3/6/16) — entries like "fractional CTO/CPO" cannot honestly carry 5 tags and
// the auto-supplementer would pad them with junk.
export const TAG_MIN_SUMMARY_SECTION = 3;

// Summary v2 subsections, in recruiter-reading order.
export const SUMMARY_SECTION_KEYS = ['identity', 'credibility', 'relevance'] as const;

// Sections whose items carry their label on `title`.
const TITLE_SECTIONS = ['education', 'awards', 'presentations', 'publications', 'portfolios'];

/** True when the per-item "tags generate on Save / generating tags…" chip applies. */
export function tagChipNeeded(
  tags: string[] | undefined,
  text: string | undefined,
  min: number = TAG_MIN_ITEM,
): boolean {
  if (!(text || '').trim()) return false;
  return (tags?.length || 0) < min;
}

/** Count tags across every taggable item — detects when the async pipeline wrote tags. */
export function countItemTags(rj: any): number {
  if (!rj) return 0;
  let n = 0;
  for (const company of rj.experience || []) {
    for (const role of company.roles || []) {
      for (const b of role.bullets || []) n += (b.tags?.length || 0);
    }
  }
  for (const s of rj.skills || []) n += (s.tags?.length || 0);
  for (const section of TITLE_SECTIONS) {
    for (const it of rj[section] || []) n += (it.tags?.length || 0);
  }
  for (const p of rj.summary_phrases || []) n += (p.tags?.length || 0);
  for (const key of SUMMARY_SECTION_KEYS) {
    for (const e of rj.summary_sections?.[key] || []) n += (e.tags?.length || 0);
  }
  return n;
}

/**
 * True if a save would trigger the backend to actually add tags — i.e. some
 * taggable item is below the per-item minimum the generator targets.
 * Mirrors shared/tag_status.has_undertagged_items so we only show the
 * "generating" status and poll when tags will really change.
 */
export function needsTagGeneration(rj: any): boolean {
  if (!rj) return false;
  for (const company of rj.experience || []) {
    for (const role of company.roles || []) {
      for (const b of role.bullets || []) {
        if (tagChipNeeded(b.tags, b.text, TAG_MIN_BULLET)) return true;
      }
    }
  }
  for (const s of rj.skills || []) {
    if (tagChipNeeded(s.tags, s.title)) return true;
  }
  for (const section of TITLE_SECTIONS) {
    for (const it of rj[section] || []) {
      if (tagChipNeeded(it.tags, it.title)) return true;
    }
  }
  // Summary phrases: text lives on `text`, and excluded phrases are skipped by
  // the generator (gen_tags._per_item_jobs), so they never mark "generating".
  for (const p of rj.summary_phrases || []) {
    if (p.include !== '0' && tagChipNeeded(p.tags, p.text)) return true;
  }
  // Summary v2 subsection entries: same `text`/`include` rules as phrases, but
  // the lower min-3 threshold decides whether a save will really (re)tag them.
  for (const key of SUMMARY_SECTION_KEYS) {
    for (const e of rj.summary_sections?.[key] || []) {
      if (e.include !== '0' && tagChipNeeded(e.tags, e.text, TAG_MIN_SUMMARY_SECTION)) return true;
    }
  }
  return false;
}

/**
 * Adopt freshly generated tags into the user's (possibly edited) in-editor
 * state without clobbering edits made while generation ran. Items are matched
 * by their text/title; an item the user reworded mid-generation is left alone
 * (its tags regenerate on the next save anyway). Only below-minimum tag sets
 * are filled — user-typed tags always win.
 */
export function mergeGeneratedTags(current: ResumeJSON, freshRaw: any): ResumeJSON {
  const fresh = freshRaw || {};
  const next: any = JSON.parse(JSON.stringify(current));
  const tagMap = (items: any[], keyField: string) => {
    const m = new Map<string, string[]>();
    for (const it of items || []) {
      const k = ((it?.[keyField] || '') as string).trim();
      if (k && Array.isArray(it?.tags) && it.tags.length && !m.has(k)) m.set(k, it.tags);
    }
    return m;
  };
  const skillMap = tagMap(fresh.skills, 'title');
  for (const s of next.skills || []) {
    const k = (s.title || '').trim();
    if ((s.tags?.length || 0) < TAG_MIN_ITEM && skillMap.has(k)) s.tags = [...skillMap.get(k)!];
  }
  const bulletMap = new Map<string, string[]>();
  for (const c of fresh.experience || [])
    for (const r of c?.roles || [])
      for (const b of r?.bullets || []) {
        const k = ((b?.text || '') as string).trim();
        if (k && Array.isArray(b?.tags) && b.tags.length && !bulletMap.has(k)) bulletMap.set(k, b.tags);
      }
  for (const c of next.experience || [])
    for (const r of c?.roles || [])
      for (const b of r?.bullets || []) {
        const k = (b?.text || '').trim();
        if ((b.tags?.length || 0) < TAG_MIN_BULLET && bulletMap.has(k)) b.tags = [...bulletMap.get(k)!];
      }
  for (const section of TITLE_SECTIONS) {
    const m = tagMap(fresh[section], 'title');
    for (const it of next[section] || []) {
      const k = (it.title || '').trim();
      if ((it.tags?.length || 0) < TAG_MIN_ITEM && m.has(k)) it.tags = [...m.get(k)!];
    }
  }
  // Summary phrases key on `text` rather than `title`.
  const phraseMap = tagMap(fresh.summary_phrases, 'text');
  for (const p of next.summary_phrases || []) {
    const k = (p.text || '').trim();
    if ((p.tags?.length || 0) < TAG_MIN_ITEM && phraseMap.has(k)) p.tags = [...phraseMap.get(k)!];
  }
  // Summary v2 entries also key on `text` (NOT `id`): an entry the user reworded
  // while generation ran must keep its own state, exactly like every other item.
  for (const key of SUMMARY_SECTION_KEYS) {
    const m = tagMap(fresh.summary_sections?.[key], 'text');
    for (const e of next.summary_sections?.[key] || []) {
      const k = (e.text || '').trim();
      if ((e.tags?.length || 0) < TAG_MIN_SUMMARY_SECTION && m.has(k)) e.tags = [...m.get(k)!];
    }
  }
  return next as ResumeJSON;
}
